import React, { useState } from 'react';
import { useSelector } from 'react-redux';

const COTIZACION_API_URL = process.env.REACT_APP_COTIZACION_API_URL || '/api/sendEmail';

const createInitialFormData = () => ({
  nombreEmpresa: '',
  personaContacto: '',
  rutEmpresa: '',
  telefono: '',
  correo: '',
  faenaProyecto: '',
  mensaje: '',
});

const sanitizeField = (value) => String(value || '').trim();
const stripHtmlTags = (value) => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const inputClass =
  'w-full border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#00BEE7] focus:border-transparent pl-10 py-2.5 rounded-lg hover:border-blue-900 transition-colors duration-200 text-sm';

function FormularioCotizacion() {
  const productosCarrito = useSelector((state) => state.cart);
  const opcionesProducto = productosCarrito.opciones || {};
  const [loading, setLoading] = useState(false);
  const [alertState, setAlertState] = useState({ type: '', message: '' });
  const [formData, setFormData] = useState(createInitialFormData());

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlertState({ type: '', message: '' });

    const parsedData = productosCarrito?.cart;
    if (!parsedData || parsedData.length === 0) {
      setAlertState({ type: 'error', message: 'El carrito está vacío.' });
      return;
    }

    const sanitized = {
      nombreEmpresa: sanitizeField(formData.nombreEmpresa),
      personaContacto: sanitizeField(formData.personaContacto),
      rutEmpresa: sanitizeField(formData.rutEmpresa),
      telefono: sanitizeField(formData.telefono),
      correo: sanitizeField(formData.correo).toLowerCase(),
      faenaProyecto: sanitizeField(formData.faenaProyecto),
      mensaje: sanitizeField(formData.mensaje),
    };

    if (!sanitized.nombreEmpresa || !sanitized.personaContacto || !sanitized.rutEmpresa || !sanitized.telefono || !sanitized.correo || !sanitized.faenaProyecto) {
      setAlertState({ type: 'error', message: 'Completa todos los campos obligatorios.' });
      return;
    }

    const productosDetalle = parsedData.map((item) => {
      const id = item.product?.producto_id;
      const opts = opcionesProducto[id] || {};
      return {
        nombre: item.product?.producto_titulo || 'Producto',
        descripcion: stripHtmlTags(item.product?.producto_descripcion || ''),
        cantidad: item.quantity,
        banda: opts.banda || '',
        tipo: opts.tipo || '',
        imagen_url: item.product?.producto_imagen_url || '',
        categoria: item.product?.categoria_nombre || '',
      };
    });

    setLoading(true);

    try {
      const response = await fetch(COTIZACION_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...sanitized,
          tipo: 'cotizacion',
          fecha: new Date().toISOString(),
          productos: productosDetalle,
        }),
      });

      const responseText = await response.text();
      let payload = {};
      try {
        payload = responseText ? JSON.parse(responseText) : {};
      } catch {
        payload = {};
      }

      if (!response.ok) {
        const detail = payload?.error || payload?.details
          || (responseText ? responseText.slice(0, 300) : `HTTP ${response.status} sin detalle`);
        setAlertState({ type: 'error', message: detail });
        setLoading(false);
        return;
      }

      setAlertState({ type: 'success', message: '' });
      setFormData(createInitialFormData());
    } catch (err) {
      setAlertState({
        type: 'error',
        message: `Error de red: ${err?.message || err?.toString() || 'sin detalle'}`,
      });
    }

    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* Nombre de la empresa */}
      <div className="relative mb-3">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 text-gray-400">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
          </svg>
        </div>
        <input
          onChange={handleChange}
          value={formData.nombreEmpresa}
          type="text"
          name="nombreEmpresa"
          id="cotizacion-empresa"
          aria-label="Nombre de la empresa"
          placeholder="Nombre de la empresa (Obligatorio)"
          className={inputClass}
          autoComplete="organization"
          maxLength={120}
          required
        />
      </div>

      {/* Persona de contacto + RUT */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-2 mb-1">
        <div className="relative mb-2 w-full sm:w-1/2">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 text-gray-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
          </div>
          <input
            onChange={handleChange}
            value={formData.personaContacto}
            type="text"
            name="personaContacto"
            id="cotizacion-contacto"
            aria-label="Persona de contacto"
            placeholder="Persona de contacto (Obligatorio)"
            className={`${inputClass} sm:w-full`}
            autoComplete="name"
            maxLength={120}
            required
          />
        </div>
        <div className="relative mb-2 w-full sm:w-1/2">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 text-gray-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 9h3.75M15 12h3.75M15 15h3.75M4.5 19.5h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5zm6-10.125a1.875 1.875 0 11-3.75 0 1.875 1.875 0 013.75 0zm1.294 6.336a6.721 6.721 0 01-3.17.789 6.721 6.721 0 01-3.168-.789 3.376 3.376 0 016.338 0z" />
            </svg>
          </div>
          <input
            onChange={handleChange}
            value={formData.rutEmpresa}
            type="text"
            name="rutEmpresa"
            id="cotizacion-rut"
            aria-label="RUT empresa"
            placeholder="RUT empresa (Obligatorio)"
            className={`${inputClass} sm:w-full`}
            autoComplete="off"
            maxLength={12}
            pattern="^[0-9]{7,8}-[0-9kK]{1}$"
            title="Formato: 12345678-9"
            required
          />
        </div>
      </div>

      {/* Teléfono + Correo */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-2 mb-1">
        <div className="relative mb-2 w-full sm:w-1/2">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 text-gray-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
            </svg>
          </div>
          <input
            onChange={handleChange}
            value={formData.telefono}
            type="tel"
            name="telefono"
            id="cotizacion-telefono"
            aria-label="Teléfono de contacto"
            placeholder="Teléfono (Obligatorio)"
            className={`${inputClass} sm:w-full`}
            autoComplete="tel"
            inputMode="tel"
            maxLength={15}
            pattern="^[+0-9\s()\-]{7,15}$"
            title="Ingresa un teléfono válido"
            required
          />
        </div>
        <div className="relative mb-2 w-full sm:w-1/2">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 text-gray-400">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>
          <input
            onChange={handleChange}
            value={formData.correo}
            type="email"
            name="correo"
            id="cotizacion-correo"
            aria-label="Correo electrónico"
            placeholder="Correo electrónico (Obligatorio)"
            className={`${inputClass} sm:w-full`}
            autoComplete="email"
            required
          />
        </div>
      </div>

      {/* Faena o proyecto */}
      <div className="relative mb-3">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 text-gray-400">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 7.125C2.25 6.504 2.754 6 3.375 6h6c.621 0 1.125.504 1.125 1.125v3.75c0 .621-.504 1.125-1.125 1.125h-6a1.125 1.125 0 01-1.125-1.125v-3.75zM14.25 8.625c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v8.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-8.25zM3.75 16.125c0-.621.504-1.125 1.125-1.125h5.25c.621 0 1.125.504 1.125 1.125v2.25c0 .621-.504 1.125-1.125 1.125h-5.25a1.125 1.125 0 01-1.125-1.125v-2.25z" />
          </svg>
        </div>
        <input
          onChange={handleChange}
          value={formData.faenaProyecto}
          type="text"
          name="faenaProyecto"
          id="cotizacion-faena"
          aria-label="Faena o proyecto"
          placeholder="Faena o proyecto (Obligatorio)"
          className={inputClass}
          maxLength={140}
          required
        />
      </div>

      {/* Mensaje adicional */}
      <div className="relative mb-3">
        <div className="absolute left-0 top-3 flex items-start pl-3 pointer-events-none">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 text-gray-400">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
          </svg>
        </div>
        <textarea
          onChange={handleChange}
          value={formData.mensaje}
          name="mensaje"
          id="cotizacion-mensaje"
          aria-label="Mensaje adicional"
          placeholder="Mensaje adicional (opcional)"
          className={`${inputClass} pt-3 resize-none`}
          rows="5"
          maxLength={1000}
        />
      </div>

      <p className="mb-3 text-xs text-gray-500">
        Campos obligatorios indicados en el placeholder. Formato de RUT: 12345678-9.
      </p>

      {/* Botón */}
      <button
        type="submit"
        disabled={loading || !productosCarrito?.cart?.length}
        aria-busy={loading}
        className={`w-full flex items-center justify-center gap-2.5 font-extrabold py-3.5 px-6 rounded-xl text-base transition-all duration-200 shadow-lg ${
          // eslint-disable-next-line no-nested-ternary
          loading ? 'bg-[#0f274b]/70 text-white cursor-not-allowed' : !productosCarrito?.cart?.length ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none' : 'bg-[#0f274b] hover:bg-[#00BEE7] text-white hover:text-[#0f274b] shadow-[#0f274b]/20 hover:shadow-[#00BEE7]/20'
        }`}
      >
        {loading ? (
          <>
            <svg className="h-5 w-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Enviando cotización...
          </>
        ) : (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
            {!productosCarrito?.cart?.length ? 'Agrega productos primero' : 'Enviar cotización'}
          </>
        )}
      </button>

      {/* Alerta de éxito */}
      {alertState.type === 'success' && (
        <div className="relative mt-5 overflow-hidden rounded-2xl border border-emerald-200/80 bg-white px-4 py-4 text-emerald-900 shadow-[0_14px_32px_-24px_rgba(5,150,105,0.7)]" role="alert">
          <div className="pointer-events-none absolute inset-y-0 left-0 w-1.5 bg-emerald-500" />
          <div className="pl-3 sm:pl-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-white ring-4 ring-emerald-100">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" className="h-4 w-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-base font-bold text-emerald-900">¡Cotización enviada con éxito!</p>
                <p className="mt-1 text-sm leading-relaxed text-emerald-800">
                  Gracias por cotizar con nosotros. Un asesor comercial revisará tu solicitud y te contactará a la brevedad.
                </p>
                <p className="mt-2 text-xs font-semibold tracking-wide text-emerald-700">
                  Tiempo de respuesta estimado: dentro del horario hábil.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerta de error */}
      {alertState.type === 'error' && (
        <div className="relative mt-5 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700" role="alert">
          <strong className="font-bold">No se pudo enviar. </strong>
          <span className="block sm:inline">{alertState.message || 'Por favor intenta nuevamente.'}</span>
        </div>
      )}
    </form>
  );
}

export default FormularioCotizacion;
